import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  FiAlertTriangle,
  FiInfo,
  FiSearch,
  FiArrowLeft,
  FiX,
  FiCheck,
  FiCopy,
  FiChevronDown,
  FiChevronUp,
  FiShield,
  FiHelpCircle,
  FiExternalLink,
} from "react-icons/fi";
import {
  MdOutlineAccountBalance,
  MdBlock,
  MdCheckCircle,
  MdOutlineAccessTime,
  MdPhone,
} from "react-icons/md";
import {
  TbCurrencyRupee,
  TbAlertOctagon,
  TbShieldCheck,
  TbClockHour4,
  TbCalendarDue,
  TbRefresh,
} from "react-icons/tb";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import axios from "../../Utils/axiosInstance";
import { useAuth } from "../../Components/Context/AuthContext";
import { assets } from "../../Assets/assets";
import AppLoader from "../../Components/Common/AppLoader";
import Pagination from "../../Components/Common/Pagination";
import PenaltyPaymentModal from "../../Components/UserSideComponents/SupplierPenalties/PenaltyPaymentModal";
import PenaltyInfoModal from "../../Components/UserSideComponents/SupplierPenalties/PenaltyInfoModal";

const TABS = [
  { label: "All", status: "" },
  { label: "Pending", status: "Pending" },
  { label: "Paid History", status: "Paid" },
];

const getPenaltyStatusConfig = (penalty) => {
  if (penalty.paymentStatus === "Paid") {
    return {
      label: "PAID",
      badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
      dotClass: "bg-emerald-500",
      accentBorder: "border-l-emerald-500",
    };
  }
  if (penalty.paymentStatus === "Waived") {
    return {
      label: "WAIVED",
      badgeClass: "bg-blue-50 text-blue-700 border-blue-200",
      dotClass: "bg-blue-500",
      accentBorder: "border-l-blue-500",
    };
  }
  if (penalty.paymentStatus === "Cancelled") {
    return {
      label: "CANCELLED",
      badgeClass: "bg-gray-100 text-gray-700 border-gray-200",
      dotClass: "bg-gray-400",
      accentBorder: "border-l-gray-400",
    };
  }
  if (penalty.isBlocking) {
    return {
      label: "BLOCKING",
      badgeClass: "bg-red-50 text-red-700 border-red-200",
      dotClass: "bg-red-600 animate-ping-slow",
      accentBorder: "border-l-red-600",
    };
  }
  if (penalty.isPastDue) {
    return {
      label: "OVERDUE",
      badgeClass: "bg-rose-50 text-rose-700 border-rose-200",
      dotClass: "bg-rose-500",
      accentBorder: "border-l-rose-500",
    };
  }
  if (penalty.daysUntilDue <= 1) {
    return {
      label: "URGENT",
      badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
      dotClass: "bg-amber-500",
      accentBorder: "border-l-amber-500",
    };
  }
  return {
    label: "PENDING",
    badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
    dotClass: "bg-amber-500",
    accentBorder: "border-l-amber-400",
  };
};

const SupplierPenalties = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const userId = user?._id || user?.id;

  const [activeTab, setActiveTab] = useState("All");
  const [penalties, setPenalties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("due_soon"); // "due_soon" | "amount_desc" | "amount_asc" | "newest"
  const [expandedPenaltyId, setExpandedPenaltyId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  // Modals
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentTargetPenalties, setPaymentTargetPenalties] = useState([]);
  const [infoModalOpen, setIsInfoModalOpen] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState(null);

  // Fetch penalties
  const fetchPenalties = useCallback(
    async (currentPage = 1, isManual = false) => {
      if (!userId) return;
      if (isManual) setRefreshing(true);
      else setLoading(true);

      try {
        const currentTabObj = TABS.find((t) => t.label === activeTab);
        let url = `/penalties/user/${userId}?page=${currentPage}&limit=${limit}`;

        if (currentTabObj?.status) {
          url += `&status=${currentTabObj.status}`;
        }

        const res = await axios.get(url);
        if (res.data?.success) {
          const list = res.data.data || [];
          setPenalties(list);
          setPagination(res.data.pagination || null);

          // URL query param support for deep-linking
          const params = new URLSearchParams(location.search);
          const autoSelectId = params.get("penaltyId") || params.get("highlight");
          if (autoSelectId && list.length > 0) {
            const found = list.find(
              (p) => p._id === autoSelectId || p.uniquePenaltyId === autoSelectId
            );
            if (found) {
              setExpandedPenaltyId(found._id);
              if (found.paymentStatus === "Pending") {
                setSelectedIds([found._id]);
              }
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch penalties:", err);
        toast.error(err.response?.data?.message || "Failed to load penalties");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [userId, activeTab, limit, location.search]
  );

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
    fetchPenalties(1);
  }, [activeTab, fetchPenalties]);

  useEffect(() => {
    if (page > 1) {
      fetchPenalties(page);
    }
  }, [page, fetchPenalties]);

  // Derived lists and metrics
  const pendingPenalties = useMemo(
    () =>
      penalties.filter(
        (p) => p.paymentStatus === "Pending" || p.paymentStatus === "Failed"
      ),
    [penalties]
  );

  const blockingPenalties = useMemo(
    () => pendingPenalties.filter((p) => p.isBlocking || p.isPastDue),
    [pendingPenalties]
  );

  const paidPenalties = useMemo(
    () => penalties.filter((p) => p.paymentStatus === "Paid"),
    [penalties]
  );

  const totalPendingAmount = useMemo(
    () =>
      pendingPenalties.reduce(
        (sum, p) => sum + (p.amounts?.totalAmount || 0),
        0
      ),
    [pendingPenalties]
  );

  const totalPaidAmount = useMemo(
    () =>
      paidPenalties.reduce((sum, p) => sum + (p.amounts?.totalAmount || 0), 0),
    [paidPenalties]
  );

  // Filtered & sorted penalties for current list
  const displayPenalties = useMemo(() => {
    let list = [...penalties];

    // Search query filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (p) =>
          p.uniquePenaltyId?.toLowerCase().includes(q) ||
          p._id?.toLowerCase().includes(q) ||
          p.penaltyReason?.toLowerCase().includes(q) ||
          p.penaltyType?.toLowerCase().includes(q) ||
          (typeof p.orderId === "object" &&
            p.orderId?.orderNumber?.toLowerCase().includes(q))
      );
    }

    // Sorting
    list.sort((a, b) => {
      if (sortBy === "due_soon") {
        return new Date(a.dueDate || 0) - new Date(b.dueDate || 0);
      }
      if (sortBy === "amount_desc") {
        return (b.amounts?.totalAmount || 0) - (a.amounts?.totalAmount || 0);
      }
      if (sortBy === "amount_asc") {
        return (a.amounts?.totalAmount || 0) - (b.amounts?.totalAmount || 0);
      }
      if (sortBy === "newest") {
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
      return 0;
    });

    return list;
  }, [penalties, searchQuery, sortBy]);

  // Selection handlers
  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAll = () => {
    const selectablePending = displayPenalties.filter(
      (p) => p.paymentStatus === "Pending" || p.paymentStatus === "Failed"
    );
    if (selectedIds.length === selectablePending.length && selectablePending.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectablePending.map((p) => p._id));
    }
  };

  const selectedPenaltiesList = useMemo(
    () => penalties.filter((p) => selectedIds.includes(p._id)),
    [penalties, selectedIds]
  );

  const selectedTotalAmount = useMemo(
    () =>
      selectedPenaltiesList.reduce(
        (sum, p) => sum + (p.amounts?.totalAmount || 0),
        0
      ),
    [selectedPenaltiesList]
  );

  // Initiate single penalty payment
  const handlePaySingle = (penalty) => {
    setPaymentTargetPenalties([penalty]);
    setPaymentModalOpen(true);
  };

  // Initiate checkout for all selected
  const handlePaySelected = () => {
    if (!selectedPenaltiesList.length) {
      toast.error("Please select at least one penalty to pay");
      return;
    }
    setPaymentTargetPenalties(selectedPenaltiesList);
    setPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    setSelectedIds([]);
    fetchPenalties(1);
  };

  const copyToClipboard = (text, label = "ID") => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  if (loading && penalties.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center justify-center p-6 font-Urbanist">
        <AppLoader fullScreen message="Loading supplier penalties..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-28 md:pb-16 font-Urbanist">
      <div className="2xl:max-w-7xl mx-auto px-3 sm:px-6 md:px-8 py-4 sm:py-6 space-y-5">
        {/* ══════════════════════════════════════════════════════════════════
            HEADER & ACTIONS
        ══════════════════════════════════════════════════════════════════ */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="w-8 h-8 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-primaryColor hover:border-primaryColor/30 transition shadow-xs sm:hidden"
                title="Go back"
              >
                <FiArrowLeft size={16} />
              </button>
              <h1 className="text-xl sm:text-2xl font-black text-primaryColor flex items-center gap-2 tracking-tight">
                <TbAlertOctagon size={26} className="text-primaryColor" />
                Supplier Penalties
              </h1>
              <button
                type="button"
                onClick={() => setIsInfoModalOpen(true)}
                className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-primaryColor hover:bg-primaryColor/10 flex items-center justify-center transition"
                title="Policy & Support Info"
              >
                <FiInfo size={14} />
              </button>
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm mt-0.5 max-w-xl">
              Manage return & dispute penalties. Settle pending dues instantly via
              Razorpay to maintain healthy account status.
            </p>
          </div>

          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              type="button"
              onClick={() => fetchPenalties(page, true)}
              disabled={refreshing || loading}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-bold text-gray-700 dark:text-gray-200 hover:border-primaryColor/30 hover:text-primaryColor shadow-xs transition disabled:opacity-50"
            >
              <TbRefresh
                size={16}
                className={refreshing ? "animate-spin text-primaryColor" : ""}
              />
              <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
            </button>

            <button
              type="button"
              onClick={() => setIsInfoModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-primaryColor/10 text-primaryColor hover:bg-primaryColor/20 text-xs font-bold transition"
            >
              <FiHelpCircle size={15} />
              <span>Help & Rules</span>
            </button>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            BLOCKING & OVERDUE ALERT BANNER (IF ANY)
        ══════════════════════════════════════════════════════════════════ */}
        {blockingPenalties.length > 0 && (
          <div className="rounded-2xl bg-gradient-to-r from-red-600 via-rose-600 to-red-700 text-white p-4 sm:p-5 shadow-lg shadow-red-500/15 animate-fadeIn">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MdBlock size={22} className="text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-black uppercase tracking-wider bg-white/20 px-2 py-0.5 rounded-md">
                      Action Required
                    </span>
                    <span className="text-xs font-medium text-white/80">
                      {blockingPenalties.length}{" "}
                      {blockingPenalties.length === 1
                        ? "Overdue Penalty"
                        : "Overdue Penalties"}
                    </span>
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-white mt-1">
                    Your account has active or pending restrictions
                  </h3>
                  <p className="text-xs text-white/90 mt-0.5 leading-normal max-w-2xl">
                    Overdue penalties restrict quotation submissions and app
                    features. Clear your outstanding obligations now to restore
                    full account capabilities instantly.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 self-end sm:self-center flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setPaymentTargetPenalties(blockingPenalties);
                    setPaymentModalOpen(true);
                  }}
                  className="px-4 py-2.5 rounded-xl bg-white text-red-700 font-extrabold text-xs sm:text-sm shadow-md hover:bg-white/90 transition flex items-center gap-1.5"
                >
                  <TbShieldCheck size={18} />
                  <span>
                    Pay Outstanding (₹
                    {blockingPenalties
                      .reduce(
                        (sum, p) => sum + (p.amounts?.totalAmount || 0),
                        0
                      )
                      .toLocaleString("en-IN")}
                    )
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            KPI METRIC CARDS
        ══════════════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Card 1: Pending Penalties */}
          <div className="p-4 rounded-2xl bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-900/40 shadow-xs space-y-1">
            <div className="flex items-center justify-between text-xs font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider">
              <span>Pending Dues</span>
              <TbAlertOctagon size={18} />
            </div>
            <div className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white">
              ₹{totalPendingAmount.toLocaleString("en-IN")}
            </div>
            <p className="text-[11px] text-gray-500">
              {pendingPenalties.length}{" "}
              {pendingPenalties.length === 1 ? "penalty" : "penalties"} awaiting
              payment
            </p>
          </div>

          {/* Card 2: Blocking / Overdue */}
          <div className="p-4 rounded-2xl bg-white dark:bg-gray-900 border border-red-200 dark:border-red-900/40 shadow-xs space-y-1">
            <div className="flex items-center justify-between text-xs font-bold text-red-700 dark:text-red-400 uppercase tracking-wider">
              <span>Blocking / Overdue</span>
              <MdBlock size={18} />
            </div>
            <div className="text-xl sm:text-2xl font-black text-red-600 dark:text-red-400">
              {blockingPenalties.length}
            </div>
            <p className="text-[11px] text-gray-500">
              {blockingPenalties.length > 0
                ? "Immediate settlement required"
                : "No blocking restrictions"}
            </p>
          </div>

          {/* Card 3: Cleared / Paid */}
          <div className="p-4 rounded-2xl bg-white dark:bg-gray-900 border border-emerald-200 dark:border-emerald-900/40 shadow-xs space-y-1">
            <div className="flex items-center justify-between text-xs font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">
              <span>Settled / Paid</span>
              <MdCheckCircle size={18} />
            </div>
            <div className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white">
              ₹{totalPaidAmount.toLocaleString("en-IN")}
            </div>
            <p className="text-[11px] text-gray-500">
              {paidPenalties.length} completed transactions
            </p>
          </div>

          {/* Card 4: Total Record Count */}
          <div className="p-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-xs space-y-1">
            <div className="flex items-center justify-between text-xs font-bold text-gray-500 uppercase tracking-wider">
              <span>Total Records</span>
              <TbCalendarDue size={18} />
            </div>
            <div className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white">
              {pagination?.total || penalties.length}
            </div>
            <p className="text-[11px] text-gray-500">Lifetime penalty count</p>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            FILTER TABS & CONTROLS
        ══════════════════════════════════════════════════════════════════ */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-3 sm:p-4 shadow-xs space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {/* Filter Tabs */}
            <div className="flex items-center gap-1.5 p-1 bg-gray-100 dark:bg-gray-800/80 rounded-xl overflow-x-auto no-scrollbar">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.label;
                const badgeCount =
                  tab.label === "Pending"
                    ? pendingPenalties.length
                    : tab.label === "Paid History"
                    ? paidPenalties.length
                    : penalties.length;

                return (
                  <button
                    key={tab.label}
                    onClick={() => setActiveTab(tab.label)}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                      isActive
                        ? "bg-white dark:bg-gray-900 text-primaryColor shadow-xs"
                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900"
                    }`}
                  >
                    <span>{tab.label}</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.2 rounded-full font-extrabold ${
                        isActive
                          ? "bg-primaryColor/10 text-primaryColor"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {badgeCount}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Select All Checkbox (when in Pending tab) */}
            {activeTab === "Pending" && displayPenalties.length > 0 && (
              <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-gray-700 dark:text-gray-300 self-start sm:self-auto select-none">
                <input
                  type="checkbox"
                  checked={
                    selectedIds.length > 0 &&
                    selectedIds.length ===
                      displayPenalties.filter(
                        (p) =>
                          p.paymentStatus === "Pending" ||
                          p.paymentStatus === "Failed"
                      ).length
                  }
                  onChange={handleToggleSelectAll}
                  className="w-4 h-4 rounded text-primaryColor focus:ring-primaryColor accent-primaryColor cursor-pointer"
                />
                <span>
                  Select All Pending (
                  {
                    displayPenalties.filter(
                      (p) =>
                        p.paymentStatus === "Pending" ||
                        p.paymentStatus === "Failed"
                    ).length
                  }
                  )
                </span>
              </label>
            )}
          </div>

          {/* Search and Sort controls */}
          <div className="flex flex-col sm:flex-row items-center gap-3 pt-2 border-t border-gray-100 dark:border-gray-800">
            {/* Search Input */}
            <div className="relative flex-1 w-full">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by Penalty ID, Order ID, or reason..."
                className="w-full pl-9 pr-8 py-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl text-xs text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:bg-white dark:focus:bg-gray-800 focus:border-primaryColor outline-none transition"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FiX size={14} />
                </button>
              )}
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <span className="text-xs text-gray-400 font-semibold whitespace-nowrap hidden sm:inline">
                Sort:
              </span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full sm:w-auto px-3 py-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl text-xs text-gray-700 dark:text-gray-300 font-medium focus:border-primaryColor outline-none transition cursor-pointer"
              >
                <option value="due_soon">Due Date (Earliest First)</option>
                <option value="amount_desc">Amount (High to Low)</option>
                <option value="amount_asc">Amount (Low to High)</option>
                <option value="newest">Created (Newest First)</option>
              </select>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            PENALTIES LIST
        ══════════════════════════════════════════════════════════════════ */}
        {loading ? (
          <div className="py-16 flex flex-col items-center justify-center">
            <AppLoader fullScreen={false} message="Loading penalties..." />
          </div>
        ) : displayPenalties.length === 0 ? (
          <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-12 text-center flex flex-col items-center justify-center">
            <img
              src={assets.noordervector}
              alt="No Penalties"
              className="w-48 sm:w-60 mb-4 opacity-80"
            />
            <h3 className="text-base sm:text-lg font-bold text-gray-800 dark:text-gray-200">
              {searchQuery
                ? "No matching penalties found"
                : activeTab === "Pending"
                ? "No Pending Penalties"
                : "No Penalty Records Found"}
            </h3>
            <p className="text-xs text-gray-400 mt-1 max-w-sm">
              {searchQuery
                ? `No records matched your search "${searchQuery}". Try modifying your search keywords.`
                : activeTab === "Pending"
                ? "Great news! You have cleared all dues. Your supplier account is in excellent standing."
                : "There are no penalties associated with your supplier account at this time."}
            </p>
          </div>
        ) : (
          <div className="space-y-3.5">
            {displayPenalties.map((penalty) => {
              const config = getPenaltyStatusConfig(penalty);
              const isSelected = selectedIds.includes(penalty._id);
              const isExpanded = expandedPenaltyId === penalty._id;
              const isPending =
                penalty.paymentStatus === "Pending" ||
                penalty.paymentStatus === "Failed";

              const orderNum =
                typeof penalty.orderId === "object"
                  ? penalty.orderId?.orderNumber
                  : penalty.orderId;

              return (
                <div
                  key={penalty._id}
                  className={`relative rounded-2xl bg-white dark:bg-gray-900 border transition-all duration-200 overflow-hidden shadow-xs ${
                    isSelected
                      ? "border-primaryColor ring-2 ring-primaryColor/10"
                      : "border-gray-200 dark:border-gray-800 hover:border-primaryColor/40"
                  } border-l-4 ${config.accentBorder}`}
                >
                  <div className="p-4 sm:p-5 space-y-3">
                    {/* Top Row: Select Checkbox, ID, Badges & Total Amount */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                      <div className="flex items-center gap-3">
                        {isPending && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelect(penalty._id)}
                            className="w-4 h-4 rounded text-primaryColor focus:ring-primaryColor accent-primaryColor cursor-pointer"
                            title="Select for payment"
                          />
                        )}

                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold text-xs sm:text-sm text-gray-900 dark:text-white">
                            {penalty.uniquePenaltyId ||
                              `#${penalty._id.slice(-8).toUpperCase()}`}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              copyToClipboard(
                                penalty.uniquePenaltyId || penalty._id,
                                "Penalty ID"
                              )
                            }
                            className="text-gray-400 hover:text-gray-600 transition"
                            title="Copy Penalty ID"
                          >
                            <FiCopy size={13} />
                          </button>

                          <span
                            className={`inline-flex items-center gap-1.5 text-[10px] font-extrabold px-2 py-0.5 rounded-md border uppercase ${config.badgeClass}`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${config.dotClass}`}
                            />
                            {config.label}
                          </span>

                          {orderNum && (
                            <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-md">
                              Order #{orderNum}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right Amount & Due countdown */}
                      <div className="flex items-center justify-between sm:justify-end gap-3 self-end sm:self-auto w-full sm:w-auto">
                        <div className="text-right">
                          <div className="text-lg sm:text-xl font-black text-gray-900 dark:text-white flex items-baseline justify-end">
                            ₹
                            {(
                              penalty.amounts?.totalAmount || 0
                            ).toLocaleString("en-IN")}
                          </div>
                          {isPending && (
                            <div className="text-[11px] font-bold flex items-center justify-end gap-1 text-gray-500">
                              <MdOutlineAccessTime size={13} />
                              {penalty.daysUntilDue === 0 ? (
                                <span className="text-red-600 font-extrabold">
                                  Due Today
                                </span>
                              ) : penalty.isPastDue ? (
                                <span className="text-rose-600 font-extrabold">
                                  Overdue
                                </span>
                              ) : (
                                <span>Due in {penalty.daysUntilDue} days</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Middle Row: Description & Order context */}
                    <div className="flex items-center justify-between gap-2 text-xs text-gray-600 dark:text-gray-300">
                      <div>
                        <span className="text-gray-400 text-[11px] uppercase tracking-wider font-semibold block">
                          Penalty Reason
                        </span>
                        <span className="font-semibold text-gray-800 dark:text-gray-200">
                          {penalty.penaltyReason?.replace(/_/g, " ") ||
                            penalty.penaltyType?.replace(/_/g, " ") ||
                            "Supplier Return Settlement"}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          setExpandedPenaltyId(
                            isExpanded ? null : penalty._id
                          )
                        }
                        className="inline-flex items-center gap-1 text-xs font-bold text-primaryColor hover:underline"
                      >
                        <span>{isExpanded ? "Less Details" : "View Breakdown"}</span>
                        {isExpanded ? (
                          <FiChevronUp size={15} />
                        ) : (
                          <FiChevronDown size={15} />
                        )}
                      </button>
                    </div>

                    {/* Expandable Section: Full Breakdown */}
                    {isExpanded && (
                      <div className="pt-3.5 border-t border-gray-100 dark:border-gray-800 space-y-3 animate-fadeIn text-xs">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3.5 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700/60">
                          <div>
                            <span className="text-[10px] uppercase font-bold text-gray-400 block">
                              Refund Amount
                            </span>
                            <span className="font-bold text-gray-800 dark:text-gray-200">
                              ₹
                              {(
                                penalty.amounts?.refundAmount || 0
                              ).toLocaleString("en-IN")}
                            </span>
                          </div>

                          {penalty.amounts?.ofmAllowanceAmount > 0 && (
                            <div>
                              <span className="text-[10px] uppercase font-bold text-gray-400 block">
                                OFM Allowance
                              </span>
                              <span className="font-bold text-gray-800 dark:text-gray-200">
                                ₹
                                {(
                                  penalty.amounts.ofmAllowanceAmount
                                ).toLocaleString("en-IN")}
                              </span>
                            </div>
                          )}

                          <div>
                            <span className="text-[10px] uppercase font-bold text-gray-400 block">
                              Due Date
                            </span>
                            <span className="font-bold text-gray-800 dark:text-gray-200">
                              {penalty.dueDate
                                ? new Date(penalty.dueDate).toLocaleDateString(
                                    "en-IN",
                                    {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                    }
                                  )
                                : "—"}
                            </span>
                          </div>

                          <div>
                            <span className="text-[10px] uppercase font-bold text-gray-400 block">
                              Created On
                            </span>
                            <span className="font-bold text-gray-800 dark:text-gray-200">
                              {penalty.createdAt
                                ? new Date(penalty.createdAt).toLocaleDateString(
                                    "en-IN",
                                    {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                    }
                                  )
                                : "—"}
                            </span>
                          </div>
                        </div>

                        {/* Paid Information (if paid) */}
                        {penalty.paymentStatus === "Paid" && (
                          <div className="p-3 rounded-xl bg-emerald-50 text-emerald-900 border border-emerald-200/70 text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <MdCheckCircle
                                size={18}
                                className="text-emerald-600 flex-shrink-0"
                              />
                              <span>
                                Settled on{" "}
                                <strong>
                                  {penalty.paidAt
                                    ? new Date(penalty.paidAt).toLocaleDateString(
                                        "en-IN",
                                        {
                                          day: "numeric",
                                          month: "short",
                                          year: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        }
                                      )
                                    : "Recently"}
                                </strong>
                              </span>
                            </div>
                            {penalty.paymentTransactionId && (
                              <span className="font-mono text-[11px] text-emerald-800 bg-white/70 px-2 py-0.5 rounded border border-emerald-200">
                                Ref: {penalty.paymentTransactionId}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Blocking Alert (if blocking) */}
                        {penalty.isBlocking && (
                          <div className="p-3 rounded-xl bg-red-50 text-red-800 border border-red-200 text-xs flex items-center gap-2">
                            <MdBlock size={18} className="text-red-600 flex-shrink-0" />
                            <span>
                              <strong>Account Blocking Active:</strong> Live
                              orders and quotation features are restricted until
                              this penalty is paid.
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Bottom Actions Row */}
                    <div className="pt-2 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3">
                      <div className="text-[11px] text-gray-400">
                        Type: {penalty.penaltyType?.replace(/_/g, " ") || "Standard"}
                      </div>

                      {isPending ? (
                        <button
                          type="button"
                          onClick={() => handlePaySingle(penalty)}
                          className="px-4 py-1.5 rounded-xl bg-primaryColor hover:bg-[#651a2e] text-white font-bold text-xs shadow-xs transition flex items-center gap-1.5"
                        >
                          <TbShieldCheck size={16} />
                          <span>
                            Pay ₹
                            {(
                              penalty.amounts?.totalAmount || 0
                            ).toLocaleString("en-IN")}
                          </span>
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-bold">
                          <MdCheckCircle size={15} /> Payment Complete
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            PAGINATION
        ══════════════════════════════════════════════════════════════════ */}
        {pagination && pagination.totalPages > 1 && (
          <div className="pt-2 flex justify-center">
            <Pagination
              currentPage={page}
              totalPages={pagination.totalPages}
              onPageChange={(p) => setPage(p)}
            />
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          STICKY / FLOATING CHECKOUT BAR (WHEN PENALTIES ARE SELECTED)
      ══════════════════════════════════════════════════════════════════ */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-t border-gray-200 dark:border-gray-800 shadow-2xl p-3 sm:p-4 animate-fadeIn">
          <div className="2xl:max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primaryColor/10 flex items-center justify-center text-primaryColor font-black text-sm">
                {selectedIds.length}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {selectedIds.length}{" "}
                    {selectedIds.length === 1 ? "Penalty" : "Penalties"} Selected
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedIds([])}
                    className="text-[11px] text-gray-400 hover:text-red-500 underline font-semibold transition"
                  >
                    Clear All
                  </button>
                </div>
                <div className="text-xl font-black text-primaryColor flex items-baseline gap-1">
                  ₹{selectedTotalAmount.toLocaleString("en-IN")}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2.5 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                className="hidden sm:inline-block px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePaySelected}
                className="flex-1 sm:flex-none px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#772037] to-[#CC234F] hover:from-[#651a2e] hover:to-[#772037] text-white font-extrabold text-sm shadow-md shadow-primaryColor/25 transition flex items-center justify-center gap-2"
              >
                <TbShieldCheck size={18} />
                <span>
                  Pay ₹{selectedTotalAmount.toLocaleString("en-IN")} via Razorpay
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          MODALS
      ══════════════════════════════════════════════════════════════════ */}
      <PenaltyPaymentModal
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        selectedPenalties={paymentTargetPenalties}
        user={user}
        onSuccess={handlePaymentSuccess}
      />

      <PenaltyInfoModal
        isOpen={infoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />
    </div>
  );
};

export default SupplierPenalties;
